import { apiClient } from './client';
import { uploadsApi } from './uploads';
import type { PatientAnamnesis, SaveInlineAnamnesisPayload } from '@/types/patient-anamnesis';

/**
 * Anamnesis 400s carry a user-safe pt-BR message (`description`) — e.g. file
 * too large, empty extraction, content too long. Entitlement errors share the
 * same shape.
 */
export const getAnamnesisApiErrorMessage = (error: unknown): string | null => {
    const description = (error as { response?: { data?: { description?: unknown } } })?.response
        ?.data?.description;
    return typeof description === 'string' && description ? description : null;
};

export const patientAnamnesisApi = {
    get: async (patientId: string): Promise<PatientAnamnesis | null> => {
        const response = await apiClient.get<{ data: PatientAnamnesis | null }>(
            `/patients/${patientId}/anamnesis`,
        );
        return (response as unknown as { data: PatientAnamnesis | null }).data;
    },

    saveInline: async (
        patientId: string,
        payload: SaveInlineAnamnesisPayload,
    ): Promise<PatientAnamnesis> => {
        const response = await apiClient.put<{ data: PatientAnamnesis }>(
            `/patients/${patientId}/anamnesis`,
            payload,
        );
        return (response as unknown as { data: PatientAnamnesis }).data;
    },

    remove: async (patientId: string): Promise<void> => {
        await apiClient.delete(`/patients/${patientId}/anamnesis`);
    },

    uploadFile: async (patientId: string, file: File): Promise<PatientAnamnesis> => {
        // The staging key goes to the anamnesis endpoint, which validates and
        // consumes the object itself — it is never sent to /uploads/confirm.
        const key = await uploadsApi.uploadToStaging({ file, intent: 'anamnesis' });

        const response = await apiClient.post<{ data: PatientAnamnesis }>(
            `/patients/${patientId}/anamnesis/upload`,
            { key, file_name: file.name },
        );
        return (response as unknown as { data: PatientAnamnesis }).data;
    },
};
